import { cn } from '@/lib/utils';

/**
 * ScripsIcon — renders a glyph from the Scrips icon font (same TTFs the
 * Flutter PM app ships). Codepoints mirror `scrips_core/lib/constants/
 * app_icons.dart` exactly so the icons in the React app are pixel-identical
 * to dev.
 *
 * Use this for action-row buttons, location markers, recurring indicators,
 * and anything else Flutter uses ScripsIcons for. For decorative icons that
 * don't have a Flutter equivalent (e.g. the avatar palette story), keep
 * lucide.
 */

export type ScripsIconName = keyof typeof SCRIPS_ICON_CODEPOINTS;

/** Codepoints from `scrips_core/lib/constants/app_icons.dart`. */
const SCRIPS_ICON_CODEPOINTS = {
  cancel: 0xe902,
  noShow: 0xe918,
  waitingRoom: 0xe919,
  walking: 0xe91a,
  cancelled: 0xe91b,
  forwardArrow: 0xe900,
  block: 0xe901,
  comment: 0xe906,
  delete: 0xe907,
  edit: 0xe908,
  externalSource: 0xe909,
  home: 0xe90a,
  list: 0xe90b,
  location: 0xe90c,
  minus: 0xe90d,
  office: 0xe90e,
  flag: 0xe90f,
  plus: 0xe910,
  recurring: 0xe911,
  reminder: 0xe912,
  resend: 0xe913,
  grid: 0xe914,
  vacation: 0xe915,
  virtual: 0xe916,
  work: 0xe917,
  archive: 0xe92d,
  check: 0xe92e,
  // Flutter `app_icons.dart` ScripsIcons.checkboxFilled = 0xe903 — the
  // current-status glyph in the Edit-Appointment "APPOINTMENT STATUS" summary.
  checkboxFilled: 0xe903,
  arrowChevronUp: 0xe92f,
  arrowChevronLeft: 0xe948,
  arrowChevronRight: 0xe949,
  chevronDown: 0xe905,
  appointment: 0xe91c,
  preview: 0xe934,
  sortArrow: 0xe935,
  starFilled: 0xe936,
  starEmpty: 0xe937,
  refresh: 0xe961,
  checkedIn: 0xe965,
  unmarkAsCheckedIn: 0xe966,
  unmarkAsArrived: 0xe967,
  // Flutter `app_icons.dart` ScripsIcons.billing = 0xe94c. Used by the
  // calendar row overflow menu "Open 'Billing' Tab" item (PROD-915,
  // Flutter `calendar_tab.dart:2650-2654`).
  billing: 0xe94c,
  // Flutter `app_icons.dart` ScripsIcons.iconPrint = 0xe997. Patient-list
  // kebab "Print Patient sticker" (`patient_list_screen.dart:847`).
  iconPrint: 0xe997,
  // Flutter `app_icons.dart` ScripsIcons.calendarToday = 0xe98f. Patient-list
  // kebab "Create Appointment" (`patient_list_screen.dart:783`).
  calendarToday: 0xe98f,
  // Flutter `app_icons.dart` ScripsIcons.iconFilter = 0xe97f. Patient
  // appointments tab "Statuses" filter affordance
  // (`patient_appointments_tab.dart:694`).
  iconFilter: 0xe97f,
  // Flutter `app_icons.dart` ScripsIcons.iconPayRefund = 0xe984. Patient
  // billing Advance-receipts kebab "Refund Payment"
  // (`patient_advance_receipts_screen.dart:480`).
  iconPayRefund: 0xe984,
} as const;

interface ScripsIconProps {
  name: ScripsIconName;
  /** Pixel size — same number is applied to font-size and width/height. */
  size?: number;
  className?: string;
  /** Inline style override — useful for color via CSS var when className
   *  can't reach the right context. */
  style?: React.CSSProperties;
  'aria-label'?: string;
}

export function ScripsIcon({
  name,
  size = 14,
  className,
  style,
  'aria-label': ariaLabel,
}: ScripsIconProps) {
  const codepoint = SCRIPS_ICON_CODEPOINTS[name];
  return (
    <span
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      // The glyph's name, for the bridge (`/* icon stroke */` in showcase-bridge.css): it paints the Lucide drawing
      // for the concept over the font glyph, so every icon has the one Lucide line weight (Antonin, 16 Oct).
      data-icon={name}
      style={{
        fontFamily: 'Scrips',
        fontSize: size,
        lineHeight: 1,
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Disable font-feature-settings that could interfere with the
        // glyph mapping (some browsers apply ligatures by default).
        fontFeatureSettings: '"liga" 0, "clig" 0',
        ...style,
      }}
      className={cn('select-none', className)}
    >
      {String.fromCodePoint(codepoint)}
    </span>
  );
}
